import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyMembers } from '../hooks/useSupabase';
import { useImageUpload } from '../hooks/useImageUpload';
import { useHouseholdAdmin } from '../hooks/useHousehold';
import { DIETARY_OPTIONS } from '../lib/dietary';
import { LoadingSpinner, ErrorState } from './ui/StateViews';
import { share } from '../lib/share';
import Avatar from './Avatar';

const AVATAR_OPTIONS = ['🧑', '👩', '👨', '👧', '👦', '👶', '🧓', '👵', '👴', '🐶', '🐱', '🦊'];

function MemberTypeToggle({ value, onChange }) {
  return (
    <div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange('adult')}
          className={`flex-1 py-2 rounded-xl font-body font-medium text-sm transition-colors ${
            value === 'adult'
              ? 'bg-lime text-canvas'
              : 'bg-surface text-ink-soft border border-hairline'
          }`}
        >
          Adult
        </button>
        <button
          type="button"
          onClick={() => onChange('kid')}
          className={`flex-1 py-2 rounded-xl font-body font-medium text-sm transition-colors ${
            value === 'kid'
              ? 'bg-lime text-canvas'
              : 'bg-surface text-ink-soft border border-hairline'
          }`}
        >
          Kid
        </button>
      </div>
      <p className="font-body text-xs text-ink-muted mt-2 leading-relaxed">
        Adults manage the household and plans. Kids (under 13) get a simpler experience an adult sets up.
      </p>
    </div>
  );
}

function AvatarPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-6 gap-2">
      {AVATAR_OPTIONS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          onClick={() => onChange(emoji)}
          className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
            value === emoji
              ? 'bg-lime/10 ring-2 ring-lime scale-110'
              : 'bg-surface hover:bg-surface'
          }`}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

function HouseholdInfoCard({ householdName, inviteCode, isAdmin, onNameUpdated }) {
  const { updateHouseholdName, loading } = useHouseholdAdmin();
  const { refreshMembership } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(householdName);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const inviteLink = `${window.location.origin}/join/${inviteCode}`;

  const handleSave = async () => {
    if (!name.trim() || name.trim() === householdName) {
      setEditing(false);
      setName(householdName);
      return;
    }
    const { error } = await updateHouseholdName(name.trim());
    if (!error) {
      await refreshMembership();
      onNameUpdated?.(name.trim());
    }
    setEditing(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
    }
  };

  const handleShare = async () => {
    const result = await share({
      title: 'Join my household on Suppertide',
      url: inviteLink,
    });
    if (result.method === 'clipboard') {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  return (
    <div className="bg-panel rounded-2xl p-5 border border-hairline">
      <div className="flex items-center gap-2 mb-3">
        {editing ? (
          <div className="flex-1 flex items-center gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') { setEditing(false); setName(householdName); }
              }}
              className="flex-1 px-3 py-1.5 rounded-lg border border-hairline bg-surface font-display text-lg font-semibold text-ink focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent"
            />
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-3 py-1.5 rounded-lg bg-lime text-canvas font-body text-sm font-medium hover:bg-lime transition-colors disabled:opacity-50"
            >
              Save
            </button>
            <button
              onClick={() => { setEditing(false); setName(householdName); }}
              className="px-3 py-1.5 rounded-lg bg-surface text-ink-soft font-body text-sm font-medium hover:bg-surface transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <h2 className="font-display text-lg font-semibold text-ink">{householdName}</h2>
            {isAdmin && (
              <button
                onClick={() => setEditing(true)}
                className="text-ink-muted hover:text-ink-soft transition-colors"
                aria-label="Edit household name"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            )}
          </>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="font-body text-sm text-ink-soft">Invite code:</span>
        <code className="font-mono text-smr text-ink bg-surface px-2 py-0.5 rounded">
          {inviteCode}
        </code>
        <button
          onClick={handleCopy}
          className="text-ink-muted hover:text-ink-soft transition-colors"
          aria-label="Copy invite code"
        >
          {copied ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-mint">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          )}
        </button>
      </div>

      <button
        onClick={handleShare}
        className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-lime hover:bg-lime active:bg-lime text-canvas font-body text-sm font-semibold transition-colors"
      >
        {linkCopied ? (
          <>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Link copied!
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share invite link
          </>
        )}
      </button>
    </div>
  );
}

function PlayerCard({ member, isCurrentUser, onOpen }) {
  const isAdult = member.type === 'adult';
  const accent = isAdult
    ? { ring: 'ring-lime/40', chip: 'bg-lime/10 text-lime', glow: 'from-lime/15' }
    : { ring: 'ring-mint/40', chip: 'bg-mint/10 text-mint', glow: 'from-mint/15' };

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative text-left bg-panel rounded-2xl p-4 border border-hairline hover:border-hairline/30 hover:shadow-lg transition-all duration-200 active:scale-[0.98] overflow-hidden"
    >
      <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full bg-gradient-to-br ${accent.glow} to-transparent`} />
      <div className="relative flex items-center gap-3">
        <div className={`shrink-0 w-12 h-12 rounded-2xl bg-surface flex items-center justify-center text-2xl ring-2 ${accent.ring} overflow-hidden`}>
          <Avatar url={member.avatar_url} fallback={member.avatar} name={member.name} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-display font-semibold text-ink truncate">{member.name.split(' ')[0]}</span>
            {isCurrentUser && <span className="font-body text-[10px] text-ink-muted">(You)</span>}
          </div>
          <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-body font-bold ${accent.chip}`}>
            {isAdult ? 'Adult' : 'Kid'}
          </span>
        </div>
      </div>
      <div className="relative mt-3 flex items-center justify-end font-body text-xs">
        <span className="font-body text-[11px] font-semibold text-lime group-hover:text-lime">Profile &rarr;</span>
      </div>
    </button>
  );
}

function PlayerProfileModal({ member, isCurrentUser, isAdmin, onSave, onArchive, onClose }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const isAdult = member.type === 'adult';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-surface/50 backdrop-blur-sm sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-br from-surface via-panel to-canvas text-ink p-6 rounded-t-3xl">
          <button
            onClick={onClose}
            aria-label="Close profile"
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-ink/15 hover:bg-ink/25 flex items-center justify-center transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-ink/15 flex items-center justify-center text-3xl ring-2 ring-lime/50 overflow-hidden"><Avatar url={member.avatar_url} fallback={member.avatar} name={member.name} /></div>
            <div className="min-w-0">
              <h3 className="font-display text-2xl font-semibold leading-tight truncate">
                {member.name}
                {isCurrentUser && <span className="font-body text-sm text-ink/60 ml-2">You</span>}
              </h3>
              <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-ink/15 text-[10px] font-body font-bold">{isAdult ? 'Adult' : 'Kid'}</span>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          {editing ? (
            <EditMemberForm
              member={member}
              onSave={async (id, updates) => { await onSave(id, updates); setEditing(false); }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <>
              {isAdmin && (
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setEditing(true)}
                    className="flex-1 py-2.5 rounded-xl bg-lime text-canvas font-body text-sm font-semibold hover:bg-lime transition-colors"
                  >
                    Edit member
                  </button>
                  {!isCurrentUser && (confirming ? (
                    <div className="flex gap-2 flex-1">
                      <button
                        onClick={() => { onArchive(member.id); onClose(); }}
                        className="flex-1 py-2.5 rounded-xl bg-red-50 text-red-600 font-body text-sm font-semibold hover:bg-red-100 transition-colors"
                      >
                        Remove
                      </button>
                      <button
                        onClick={() => setConfirming(false)}
                        className="flex-1 py-2.5 rounded-xl bg-surface text-ink-soft font-body text-sm font-semibold hover:bg-surface transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirming(true)}
                      className="px-4 py-2.5 rounded-xl bg-surface text-ink-soft font-body text-sm font-semibold hover:bg-surface transition-colors"
                    >
                      Remove
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EditMemberForm({ member, onSave, onCancel }) {
  const { householdId } = useAuth();
  const { uploading, uploadMemberAvatar } = useImageUpload();
  const [name, setName] = useState(member.name);
  const [type, setType] = useState(member.type);
  const [avatar, setAvatar] = useState(member.avatar);
  // '' = cleared (fall back to emoji on save); null = unchanged; string = new photo.
  const [avatarUrl, setAvatarUrl] = useState(member.avatar_url || null);
  const [photoError, setPhotoError] = useState(null);
  const [calories, setCalories] = useState(member.daily_cal_target);
  const [allergies, setAllergies] = useState(member.allergies || []);
  const [saving, setSaving] = useState(false);

  const toggleRestriction = (value) => {
    setAllergies(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setPhotoError(null);
    const { url, error } = await uploadMemberAvatar(file, householdId, member.id);
    if (error) { setPhotoError(error); return; }
    setAvatarUrl(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(member.id, { name: name.trim(), type, avatar, avatar_url: avatarUrl, daily_cal_target: calories, allergies });
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-surface rounded-xl p-4 space-y-3">
      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Name</label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-hairline bg-panel font-body text-sm text-ink focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent"
        />
      </div>

      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Type</label>
        <MemberTypeToggle value={type} onChange={setType} />
      </div>

      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Photo</label>
        <div className="flex items-center gap-3">
          <div className="shrink-0 w-14 h-14 rounded-2xl bg-surface ring-2 ring-hairline flex items-center justify-center text-2xl overflow-hidden">
            <Avatar url={avatarUrl || null} fallback={avatar} name={name} />
          </div>
          <div className="flex flex-col gap-1 items-start">
            <label className="cursor-pointer inline-flex items-center px-3 py-1.5 rounded-lg bg-panel border border-hairline font-body text-xs font-medium text-ink-soft hover:text-ink">
              {uploading ? 'Uploading…' : (avatarUrl ? 'Change photo' : 'Upload photo')}
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} disabled={uploading} />
            </label>
            {avatarUrl && (
              <button type="button" onClick={() => setAvatarUrl('')} className="font-body text-xs text-ink-muted underline hover:text-ink-secondary">
                Remove photo
              </button>
            )}
          </div>
        </div>
        {photoError && <p className="font-body text-xs text-red-500 mt-1">{photoError}</p>}
      </div>

      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Emoji avatar (fallback)</label>
        <AvatarPicker value={avatar} onChange={setAvatar} />
      </div>

      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Daily calorie target</label>
        <input
          type="number"
          min="500"
          max="5000"
          value={calories}
          onChange={(e) => setCalories(parseInt(e.target.value) || 2000)}
          className="w-full px-3 py-2 rounded-lg border border-hairline bg-panel font-body text-sm text-ink focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent"
        />
      </div>

      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Dietary restrictions</label>
        <div className="flex flex-wrap gap-2">
          {DIETARY_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggleRestriction(opt.value)}
              className={`px-3 py-1.5 rounded-full font-body text-xs font-medium transition-colors ${
                allergies.includes(opt.value)
                  ? 'bg-lime text-canvas'
                  : 'bg-surface text-ink-soft border border-hairline'
              }`}
            >
              {opt.emoji} {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving}
          className="flex-1 py-2 rounded-xl bg-lime text-canvas font-body text-sm font-semibold hover:bg-lime transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 rounded-xl bg-surface text-ink-soft font-body text-sm font-semibold hover:bg-surface transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function AddMemberForm({ onAdd, onCancel }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('kid');
  const [avatar, setAvatar] = useState('🧑');
  const [calories, setCalories] = useState(2000);
  const [allergies, setAllergies] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleRestriction = (value) => {
    setAllergies(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onAdd({ name: name.trim(), type, avatar, daily_cal_target: calories, allergies });
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-panel rounded-2xl p-5 border border-hairline space-y-3">
      <h3 className="font-body font-bold text-ink-soft mb-1">Add a family member</h3>

      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Name</label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          autoFocus
          className="w-full px-3 py-2 rounded-lg border border-hairline bg-surface font-body text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent"
        />
      </div>

      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Type</label>
        <MemberTypeToggle value={type} onChange={setType} />
      </div>

      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Avatar</label>
        <AvatarPicker value={avatar} onChange={setAvatar} />
      </div>

      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Daily calorie target</label>
        <input
          type="number"
          min="500"
          max="5000"
          value={calories}
          onChange={(e) => setCalories(parseInt(e.target.value) || 2000)}
          className="w-full px-3 py-2 rounded-lg border border-hairline bg-surface font-body text-sm text-ink focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent"
        />
      </div>

      <div>
        <label className="block font-body text-xs font-medium text-ink-soft mb-1">Dietary restrictions</label>
        <div className="flex flex-wrap gap-2">
          {DIETARY_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggleRestriction(opt.value)}
              className={`px-3 py-1.5 rounded-full font-body text-xs font-medium transition-colors ${
                allergies.includes(opt.value)
                  ? 'bg-lime text-canvas'
                  : 'bg-surface text-ink-soft border border-hairline'
              }`}
            >
              {opt.emoji} {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="flex-1 py-2.5 rounded-xl bg-lime text-canvas font-body text-sm font-semibold hover:bg-lime transition-colors disabled:opacity-50"
        >
          {saving ? 'Adding...' : 'Add member'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-xl bg-surface text-ink-soft font-body text-sm font-semibold hover:bg-surface transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function FamilyScreen() {
  const { householdId, currentMember, inviteCode, membership } = useAuth();
  const { members, loading, error, refetch, addMember, updateMember, archiveMember } = useFamilyMembers(householdId);
  const [profileId, setProfileId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [householdName, setHouseholdName] = useState(membership?.household_name || '');

  const isAdmin = currentMember?.type === 'adult';

  const handleSave = async (memberId, updates) => {
    await updateMember(memberId, updates);
  };

  const handleAdd = async (member) => {
    const result = await addMember(member);
    if (result) setShowAdd(false);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message="Couldn't load family members" onRetry={refetch} />;

  const adults = members.filter(m => m.type === 'adult');
  const kids = members.filter(m => m.type !== 'adult');
  const profileMember = members.find(m => m.id === profileId) || null;

  const renderRoster = (list) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {list.map(m => (
        <PlayerCard
          key={m.id}
          member={m}
          isCurrentUser={m.id === currentMember?.id}
          onOpen={() => setProfileId(m.id)}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-5 lg:space-y-6">
      <h2 className="font-display text-xl lg:text-2xl font-semibold text-ink">Family</h2>

      {/* Household Info */}
      <div className="lg:max-w-md">
        <HouseholdInfoCard
          householdName={householdName || membership?.household_name || ''}
          inviteCode={inviteCode}
          isAdmin={isAdmin}
          onNameUpdated={setHouseholdName}
        />
      </div>

      {/* Members — split adults vs kids */}
      {members.length === 0 && !showAdd && (
        <div className="bg-panel rounded-2xl border border-hairline px-5 py-6 text-center">
          <p className="font-body text-sm text-ink-muted mb-3">No family members yet</p>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="px-4 py-2 rounded-xl bg-lime hover:bg-lime text-canvas font-body text-sm font-semibold transition-colors"
            >
              Add your first family member
            </button>
          )}
        </div>
      )}

      {members.length > 0 && (
        <div className="space-y-5">
          {adults.length > 0 && (
            <section>
              <h3 className="font-display text-sm font-semibold text-ink mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-lime" /> Adults
              </h3>
              {renderRoster(adults)}
            </section>
          )}
          {kids.length > 0 && (
            <section>
              <h3 className="font-display text-sm font-semibold text-ink mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-mint" /> Kids
              </h3>
              {renderRoster(kids)}
            </section>
          )}
        </div>
      )}

      {/* Add member */}
      {isAdmin && showAdd && (
        <AddMemberForm onAdd={handleAdd} onCancel={() => setShowAdd(false)} />
      )}
      {isAdmin && !showAdd && members.length > 0 && (
        <button
          onClick={() => setShowAdd(true)}
          className="w-full py-4 rounded-2xl border-2 border-dashed border-hairline text-ink-muted hover:border-lime/40 hover:text-lime font-body font-medium transition-colors"
        >
          + Add a member
        </button>
      )}

      {profileMember && (
        <PlayerProfileModal
          member={profileMember}
          isCurrentUser={profileMember.id === currentMember?.id}
          isAdmin={isAdmin}
          onSave={handleSave}
          onArchive={archiveMember}
          onClose={() => setProfileId(null)}
        />
      )}
    </div>
  );
}
